import { useTranslation } from "react-i18next"
import { Pie, PieChart } from "recharts"

import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"
import type { DashboardPaymentTypeEnum } from "@/enums"
import { useDashboardPayment } from "@/hooks"
import { cn } from "@/lib/utils"

type DashboardPaymentMethodsChartProps = {
  dashboardType: DashboardPaymentTypeEnum
  searchParams?: SearchParamsType
}

export const DashboardPaymentMethodsChart: React.FC<
  DashboardPaymentMethodsChartProps
> = ({ dashboardType, searchParams }) => {
  const { t } = useTranslation()
  const { data: paymentMethods } = useDashboardPayment({
    dashboardType,
    ...searchParams,
  })

  const getMethodColor = (index: number) => {
    const colors = [
      {
        fill: "hsl(var(--chart-1))",
        bg: "before:bg-chart-1",
      },
      {
        fill: "hsl(var(--chart-2))",
        bg: "before:bg-chart-2",
      },
      {
        fill: "hsl(var(--chart-3))",
        bg: "before:bg-chart-3",
      },
      {
        fill: "hsl(var(--chart-4))",
        bg: "before:bg-chart-4",
      },
      {
        fill: "hsl(var(--chart-5))",
        bg: "before:bg-chart-5",
      },
    ]
    return colors[index % colors.length]
  }

  const chartData = paymentMethods?.indicator?.map(
    (indicator, index) => ({
      key: indicator.key,
      value: +indicator.value.replace("%", ""),
      fill: getMethodColor(index).fill,
      bgClass: getMethodColor(index).bg,
    })
  )

  if (!chartData || chartData.length === 0) {
    return (
      <div className="flex min-h-[200px] items-center justify-center text-sm text-muted-foreground">
        {t("pages.dashboard.noDataAvailable")}
      </div>
    )
  }

  return (
    <div>
      <ChartContainer
        config={{}}
        className="mx-auto aspect-square max-h-[200px]"
      >
        <PieChart>
          <ChartTooltip
            cursor={false}
            content={<ChartTooltipContent hideLabel />}
          />
          <Pie
            data={chartData}
            dataKey="value"
            nameKey="key"
            innerRadius={50}
          />
        </PieChart>
      </ChartContainer>
      <ul className="space-y-2">
        {chartData?.map((indicator) => (
          <li
            key={indicator.key}
            className="group flex items-center justify-between text-sm"
          >
            <span
              className={cn(
                "before:mr-1 before:inline-block before:size-2 before:rounded",
                indicator.bgClass
              )}
            >
              {indicator.key}
            </span>
            <span>{indicator.value}%</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
